import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { API_BASE_URL } from '@/utils/env';
import { BRAND, brandLabel } from '@/brand';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import '../styles/authPages.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

const ACCT_VARS: React.CSSProperties = {
  '--cream': '#FFFDF7',
  '--accent': '#FFD54F',
  '--accent-deep': '#E6B400',
  '--on-accent': '#17263A',
  '--accent-glow': 'rgba(255,213,79,.26)',
  '--accent-tint': '#ffedc0',
  '--accent-ink': '#8a6400',
  '--line': '#E8E2D2',
  '--line-strong': '#DCD3BC',
  '--card-line': '#EFE9DA',
  '--field-bg': '#FFFDF7',
  '--ring': 'rgba(230,180,0,.28)',
  '--link': '#17263A',
} as React.CSSProperties;

function AccountingLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) { setError('Please enter both email and password'); return; }
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        let errorMessage = 'Login failed. Please try again.';
        try {
          const data = await response.json();
          if (data.error) errorMessage = data.error;
          else if (response.status === 401) errorMessage = 'Invalid email or password. Please check your credentials.';
          else if (response.status === 403) errorMessage = 'Please verify your email before logging in. Check your inbox for the verification link.';
          else if (response.status === 400) errorMessage = 'Email and password are required.';
          else if (response.status === 500) errorMessage = 'Server error. Please try again later.';
        } catch {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Login failed. Please try again.');

      // Brand gate: an account created on the other domain can't sign in here.
      const acctBrand = data.profile?.brand || data.user?.brand;
      if (acctBrand && acctBrand !== BRAND) {
        setLoading(false);
        setError(`This account is registered on ${brandLabel(acctBrand)}. Please sign in there.`);
        return;
      }

      if (data.user) {
        const idToken = data.tokens?.idToken;
        localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName, brand: acctBrand || BRAND }));
        if (data.tokens) {
          const storage = rememberMe ? localStorage : sessionStorage;
          storage.setItem('authTokens', JSON.stringify(data.tokens));
          storage.setItem('token', data.tokens.idToken);
        }
        // Set accounting context
        localStorage.setItem('activeServiceView', 'accounting');

        // Sign in to Firebase client SDK so Firestore/RTDB client operations work
        if (data.customToken) {
          await signInWithCustomToken(auth, data.customToken).catch(e => console.warn('Firebase client sign-in failed:', e));
        }

        try {
          const userDataRes = await fetch(`${API_BASE_URL}/auth/user/${data.user.uid}`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          if (userDataRes.ok) {
            const userDataJson = await userDataRes.json();
            const userData = userDataJson.user;
            if (!userData?.country) { navigate('/Countrysl', { replace: true }); return; }
            if (!userData?.goalAssessment?.group) { navigate('/readiness-journey', { replace: true }); return; }
            navigate('/dashboard', { replace: true });
            return;
          } else {
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch {
          navigate(from, { replace: true });
          return;
        }
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' || err.name === 'TypeError'
        ? 'Cannot connect to server. Please check your internet connection or try again later.'
        : err.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) { setError('Failed to get Google credentials'); return; }
    setLoading(true); setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to sign in with Google');
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to sign in with Google');

      // Brand gate: an account created on the other domain can't sign in here.
      const gBrand = data.profile?.brand || data.user?.brand;
      if (gBrand && gBrand !== BRAND) {
        setLoading(false);
        setError(`This account is registered on ${brandLabel(gBrand)}. Please sign in there.`);
        return;
      }

      if (data.user) {
        localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName, firstName: data.user.firstName, lastName: data.user.lastName, photoURL: data.user.photoURL, brand: gBrand || BRAND }));
        if (data.tokens) {
          localStorage.setItem('authTokens', JSON.stringify(data.tokens));
          localStorage.setItem('token', data.tokens.idToken);
          localStorage.setItem('idToken', data.tokens.idToken);
          if (data.tokens.refreshToken) localStorage.setItem('refreshToken', data.tokens.refreshToken);
        }
        localStorage.setItem('activeServiceView', 'accounting');

        // Sign in to Firebase client SDK
        if (data.customToken) {
          await signInWithCustomToken(auth, data.customToken).catch(e => console.warn('Firebase client sign-in failed:', e));
        }

        // Check admin
        try {
          const adminsRes = await fetch(`${API_BASE_URL}/auth/admins`, { headers: { Authorization: `Bearer ${data.tokens.idToken}` } });
          const adminsData = await adminsRes.json();
          const isAdmin = adminsData.success && Array.isArray(adminsData.admins) && adminsData.admins.some((a: any) => a.uid === data.user.uid || a.email === data.user.email);
          if (isAdmin) {
            localStorage.setItem('adminUser', JSON.stringify({ email: data.user.email, role: 'admin', loginTime: new Date().toISOString() }));
            localStorage.setItem('isAdmin', 'true');
            navigate('/admin', { replace: true });
            return;
          }
        } catch {}

        try {
          const userDataRes = await fetch(`${API_BASE_URL}/auth/user/${data.user.uid}`, { headers: { Authorization: `Bearer ${data.tokens.idToken}` } });
          if (userDataRes.ok) {
            const userDataJson = await userDataRes.json();
            const userData = userDataJson.user;
            if (!userData?.country) { navigate('/Countrysl', { replace: true }); return; }
            if (!userData?.goalAssessment?.group) { navigate('/readiness-journey', { replace: true }); return; }
            navigate('/dashboard', { replace: true });
            return;
          }
          navigate('/Countrysl', { replace: true });
        } catch {
          navigate('/Countrysl', { replace: true });
        }
      }
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' || err.name === 'TypeError'
        ? 'Cannot connect to server. Please check your internet connection.'
        : err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell" style={{ ...ACCT_VARS, minHeight: '100vh' } as React.CSSProperties}>
      <div className="shell">
        {/* LEFT RAIL */}
        <aside className="rail">
          <div className="rl-logo">
            <a href="/" style={{ lineHeight: 0 }}>
              <img src={LOGO_URL} alt="Conncct" style={{ height: 34, width: 'auto', filter: 'brightness(0) invert(1)' }} />
            </a>
          </div>
          <div className="rl-body">
            <span className="rl-eyebrow">Bookkeeping · VAT · Tax</span>
            <h2>The business side of your startup, <span className="hl">handled.</span></h2>
            <p className="rl-lead">Books, VAT and UAE corporate tax — filed and compliant, never a missed FTA deadline.</p>
            <div className="mock">
              <div className="mock-top"><b>Compliance</b><span className="chip">FTA</span></div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>VAT return — Q1</b><small>Submitted to the FTA</small></span>
                <span className="st done">Done</span>
              </div>
              <div className="ck">
                <span className="d due"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" stroke="#9A6B00" strokeWidth="2.2" fill="none"/><path d="M12 7v5l3 2" stroke="#9A6B00" strokeWidth="2.3" strokeLinecap="round" fill="none"/></svg></span>
                <span className="x"><b>Corporate Tax filing</b><small>9% on profit over AED 375k</small></span>
                <span className="st due">Due soon</span>
              </div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>Bookkeeping</b><small>187 transactions reconciled</small></span>
                <span className="st done">Synced</span>
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="bar-row"><span>Investor-ready</span><b className="tnum">80%</b></div>
                <div className="bar"><i style={{ width: '80%' }}></i></div>
              </div>
            </div>
          </div>
          <div className="rl-foot">
            <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Bank-grade encryption · your data is never sold
          </div>
        </aside>

        {/* RIGHT PANE */}
        <main className="pane">
          <div className="topbar">
            <a className="top-pill" href="/accounting/signup">Create account</a>
          </div>
          <div className="center">
            <section className="card">
              <span className="eyebrow">Welcome back</span>
              <h1>Welcome <span className="hl">back</span>.</h1>
              <p className="sub">Sign in to your books, VAT and corporate-tax dashboard.</p>

              {error && <div className="err-msg">{error}</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="field">
                  <label htmlFor="email-al">Email <span style={{ color: '#E0584B', marginLeft: 2 }}>*</span></label>
                  <input className="input" id="email-al" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={loading} />
                </div>

                <div className="field has-icon">
                  <label htmlFor="pw-al">Password <span style={{ color: '#E0584B', marginLeft: 2 }}>*</span></label>
                  <div className="control">
                    <input className="input" id="pw-al" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" disabled={loading} />
                    <button type="button" className="toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      <svg viewBox="0 0 24 24">
                        {showPassword
                          ? <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/><line x1="3" y1="3" x2="21" y2="21"/></>
                          : <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></>
                        }
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="row">
                  <label className="check">
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} disabled={loading} />
                    <span className="box">
                      <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    </span>
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="link">Forgot password?</Link>
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              <div className="divider">or continue with</div>
              <div className="google-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => setError('Google Sign-In failed. Please try again or use email/password.')}
                  text="signin_with"
                  size="large"
                  width="400"
                  theme="outline"
                  shape="rectangular"
                />
              </div>

              <p className="switch">Don't have an account? <a href="/accounting/signup">Create one</a></p>
              <p className="legal">By continuing you agree to our <a href="/privacy">Terms</a> &amp; <a href="/privacy">Privacy Policy</a>.</p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default AccountingLogin;
